import { useState } from "react";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLine, Overline, SectionHeading } from "../components/Reveal";
import { AbstractVisual, ParticleField } from "../components/AbstractVisual";
import { api, WHATSAPP_URL } from "../lib/api";

const MODELS = [
  { n: "01", title: "Revenue Share", desc: "Deploy immersive attractions with shared revenue — aligned incentives, minimal upfront risk." },
  { n: "02", title: "Licensing", desc: "License individual XR experiences for your venue, season or event." },
  { n: "03", title: "Turnkey Installation", desc: "Complete hardware, software, installation and training — ready to open." },
  { n: "04", title: "Pop-Up Experiences", desc: "Temporary immersive attractions for events, campaigns and seasonal programming." },
  { n: "05", title: "Permanent Immersive Venue", desc: "We design and operate full-scale XR entertainment destinations with you." },
];

const VENUE_TYPES = ["Malls", "Museums", "Resorts", "Amusement Parks", "Tourist Destinations", "Gaming Centers", "Cultural Venues", "Real-Estate Developers", "Corporate Entertainment", "Universities", "Exhibitions", "Event Companies"];

export default function Venues() {
  const [form, setForm] = useState({ enquiry_model: "Revenue Share" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.enquire({ ...form, type: "venue-project" });
      setDone(true);
      toast.success("Project enquiry received. We'll be in touch shortly.");
    } catch {
      toast.error("Submission failed. Please check required fields.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main>
      <Seo title="For Venues & Investors" path="/venues"
        description="Bring immersive entertainment to your venue — revenue share, licensing, turnkey installation, pop-ups and permanent XR destinations for malls, museums and resorts." />
      <section className="relative min-h-[75vh] flex items-end overflow-hidden">
        <AbstractVisual hue={38} seed={71} />
        <ParticleField hue={38} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030305] via-[#030305]/40 to-[#030305]/70" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 pb-24 w-full">
          <Overline className="mb-6">For Venues & Investors</Overline>
          <h1 className="font-serif tracking-tight leading-[1.0] text-5xl md:text-7xl lg:text-8xl" data-testid="venues-headline">
            <MaskedLine delay={0.25}>Bring Immersive Entertainment</MaskedLine>
            <MaskedLine delay={0.4}><span className="italic text-zinc-400">to Your Venue.</span></MaskedLine>
          </h1>
          <div className="flex flex-wrap gap-2.5 mt-10 max-w-3xl">
            {VENUE_TYPES.map((v) => (
              <span key={v} className="px-4 py-2 rounded-full border border-white/10 text-xs font-mono text-zinc-400">{v}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-36 border-t border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <SectionHeading overline="Business Models" title={<>Five Ways to <span className="italic text-zinc-400">Open a World.</span></>} />
          <div className="mt-16 border-t border-white/[0.07]">
            {MODELS.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.05}>
                <div data-testid={`model-${m.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group grid grid-cols-12 gap-6 items-center border-b border-white/[0.07] py-9 px-2 md:px-6 hover:bg-white/[0.02] transition-colors duration-500">
                  <p className="col-span-3 md:col-span-1 font-serif text-4xl md:text-5xl text-white/[0.08] group-hover:text-white/[0.18] transition-colors duration-500">{m.n}</p>
                  <h3 className="col-span-9 md:col-span-4 font-serif text-2xl md:text-3xl tracking-tight group-hover:translate-x-2 transition-transform duration-500">{m.title}</h3>
                  <p className="col-span-12 md:col-span-7 text-sm md:text-base text-zinc-400 font-light leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-36 border-t border-white/[0.06]" data-testid="venue-form-section">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <SectionHeading overline="Discuss a Project" title="Tell Us About Your Venue" />
          {done ? (
            <Reveal className="mt-14 rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center" data-testid="venue-form-success">
              <h3 className="font-serif text-3xl mb-4">Enquiry received.</h3>
              <p className="text-zinc-400 font-light mb-8">Our venue partnerships team will respond shortly. Prefer to talk now?</p>
              <a href={WHATSAPP_URL("Hi Vrova, I just submitted a venue project enquiry.")} target="_blank" rel="noreferrer"
                data-testid="venue-success-whatsapp"
                className="inline-flex px-8 py-3.5 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors">
                Continue on WhatsApp
              </a>
            </Reveal>
          ) : (
            <form onSubmit={submit} data-testid="venue-enquiry-form" className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
              {[["name", "Name", "text", true], ["company", "Company / Venue", "text", true],
                ["email", "Email", "email", true], ["phone", "Phone", "tel", false],
                ["country", "Country", "text", false], ["city", "City", "text", false]].map(([key, label, type, req]) => (
                <div key={key}>
                  <label htmlFor={`venue-${key}`}>{label}{req ? " *" : ""}</label>
                  <input id={`venue-${key}`} data-testid={`venue-field-${key}`} type={type} required={req} value={form[key] || ""} onChange={set(key)} />
                </div>
              ))}
              <div>
                <label htmlFor="venue-type">Venue Type</label>
                <select id="venue-type" data-testid="venue-field-type" value={form.venue_type || "Mall"} onChange={set("venue_type")}>
                  {VENUE_TYPES.map((v) => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="venue-model">Model of Interest</label>
                <select id="venue-model" data-testid="venue-field-model" value={form.enquiry_model} onChange={set("enquiry_model")}>
                  {MODELS.map((m) => <option key={m.n}>{m.title}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="venue-message">Message</label>
                <textarea id="venue-message" data-testid="venue-field-message" rows={4} value={form.message || ""} onChange={set("message")} />
              </div>
              <div className="md:col-span-2">
                <button type="submit" data-testid="venue-form-submit" disabled={busy}
                  className="w-full md:w-auto px-10 py-4 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-50">
                  {busy ? "Submitting…" : "Discuss a Project"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
